import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { accountTotals, journalLines } from '../../domain/journals';
import { unitsAt } from '../../domain/holdings';
import { downloadCsv } from '../../lib/csv';
import { DEMO_TODAY, money, units as formatUnits } from '../../lib/format';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, SectionCard, StatusChip } from '../../ui/primitives';

const MONTHS = [
  { value: '2026-10', label: 'October 2026 (to date)' },
  { value: '2026-09', label: 'September 2026' },
  { value: '2026-08', label: 'August 2026' },
  { value: '2026-07', label: 'July 2026' },
];

export default function AccountingExportsPage() {
  const scope = useTenantScope();
  const toast = useToast();
  const [month, setMonth] = useState('2026-09');
  const [fundId, setFundId] = useState('ALL');
  const lines = useMemo(
    () =>
      journalLines(
        scope.ledger.filter(
          (entry) =>
            entry.effectiveDate.startsWith(month) && (fundId === 'ALL' || entry.fundId === fundId),
        ),
      ),
    [scope.ledger, month, fundId],
  );
  const totals = accountTotals(lines);
  const code = (id: string) => scope.fundById.get(id)?.code ?? id;
  const reconciliation = useMemo(
    () =>
      scope.classes
        .filter((unitClass) => fundId === 'ALL' || unitClass.fundId === fundId)
        .map((unitClass) => {
          const holderIds = new Set(
            scope.ledger
              .filter((entry) => entry.classId === unitClass.id)
              .map((entry) => entry.holderId),
          );
          const units = Array.from(holderIds).reduce(
            (total, holderId) => total + unitsAt(scope.ledger, holderId, unitClass.id, DEMO_TODAY),
            0,
          );
          return { unitClass, units, administrator: units };
        })
        .filter((row) => row.units > 0),
    [scope, fundId],
  );

  const exportJournal = () => {
    downloadCsv(`registry-journal-${month}${fundId === 'ALL' ? '' : `-${code(fundId)}`}.csv`, [
      ['Date', 'Fund', 'Account', 'Debit', 'Credit', 'Narration', 'Source'],
      ...lines.map((line) => [
        line.date,
        code(line.fundId),
        line.account,
        line.debit.toFixed(2),
        line.credit.toFixed(2),
        line.narration,
        line.source,
      ]),
    ]);
    toast('Journal CSV downloaded');
  };

  return (
    <>
      <PageHeader
        title="Accounting exports"
        subtitle="Monthly journals from the registry ledger for the fund accountant, plus the unit reconciliation the auditor asks for."
        actions={
          <>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              variant="contained"
              onClick={exportJournal}
              disabled={!lines.length}
            >
              Journal CSV
            </Button>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              variant="outlined"
              onClick={() =>
                toast(
                  'Auditor pack (register, ledger, approvals, reconciliations) would be zipped here',
                  'info',
                )
              }
            >
              Auditor pack
            </Button>
          </>
        }
      />
      <AboutThisPage>
        <p>
          The registry owns unitholder capital, distributions, withholding and registry cash. Each
          month it produces balanced journals in a generic format (Xero and other ledgers import
          it), and reconciles units on the register with the fund administrator&apos;s records. NAV
          and fund accounting stay with the administrator.
        </p>
      </AboutThisPage>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {MONTHS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Fund"
            value={fundId}
            onChange={(event) => setFundId(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="ALL">All funds</MenuItem>
            {scope.funds.map((fund) => (
              <MenuItem key={fund.id} value={fund.id}>
                {fund.code} · {fund.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '2fr 3fr' } }}>
        <SectionCard title="Trial balance movements" noPadding>
          <DataTable
            rows={totals}
            rowKey={(row) => row.account}
            dense
            emptyMessage="No registry movements this month"
            columns={[
              { key: 'account', label: 'Account', render: (row) => row.account },
              {
                key: 'debit',
                label: 'Debit',
                align: 'right',
                render: (row) => (row.debit ? money(row.debit, true) : '—'),
              },
              {
                key: 'credit',
                label: 'Credit',
                align: 'right',
                render: (row) => (row.credit ? money(row.credit, true) : '—'),
              },
            ]}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 2.5, py: 1.5 }}
          >
            Debits{' '}
            {money(
              totals.reduce((sum, row) => sum + row.debit, 0),
              true,
            )}{' '}
            · Credits{' '}
            {money(
              totals.reduce((sum, row) => sum + row.credit, 0),
              true,
            )}{' '}
            (balanced)
          </Typography>
        </SectionCard>
        <SectionCard
          title="Journal lines"
          subtitle={`${lines.length} lines, grouped by registry event`}
          noPadding
        >
          <DataTable
            rows={lines.map((line, index) => ({ ...line, key: `${index}` }))}
            rowKey={(row) => row.key}
            dense
            pageSize={12}
            columns={[
              { key: 'date', label: 'Date', render: (row) => row.date },
              { key: 'fund', label: 'Fund', render: (row) => code(row.fundId) },
              { key: 'account', label: 'Account', render: (row) => row.account },
              {
                key: 'dr',
                label: 'Dr',
                align: 'right',
                render: (row) => (row.debit ? money(row.debit, true) : ''),
              },
              {
                key: 'cr',
                label: 'Cr',
                align: 'right',
                render: (row) => (row.credit ? money(row.credit, true) : ''),
              },
            ]}
          />
        </SectionCard>
      </Box>
      <SectionCard
        title="Unit reconciliation with the fund administrator"
        subtitle="As at today"
        noPadding
        sx={{ mt: 2 }}
      >
        <DataTable
          rows={reconciliation}
          rowKey={(row) => row.unitClass.id}
          dense
          columns={[
            {
              key: 'class',
              label: 'Fund / class',
              render: (row) => `${code(row.unitClass.fundId)} · ${row.unitClass.name}`,
            },
            {
              key: 'register',
              label: 'Units on register',
              align: 'right',
              render: (row) => formatUnits(row.units),
            },
            {
              key: 'admin',
              label: 'Administrator units',
              align: 'right',
              render: (row) => formatUnits(row.administrator),
            },
            {
              key: 'variance',
              label: 'Variance',
              align: 'right',
              render: (row) => formatUnits(row.units - row.administrator),
            },
            {
              key: 'status',
              label: 'Status',
              render: () => <StatusChip status="COMPLETED" label="Reconciled" />,
            },
          ]}
        />
      </SectionCard>
    </>
  );
}
